import { useEffect, useState } from 'react';
import {
  CartesianGrid,
  Legend,
  Line,
  LineChart,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { api, formatNumber, formatRelative, formatUsd } from '../api';
import { usePolling } from '../auth';
import { Empty, Figure, Note, Panel, Spinner } from '../components/ui';
import type {
  AnalyseResponse,
  ChainExpiry,
  OptionChainResponse,
  OptionPreset,
  OptionPresetId,
  StrategyAnalysis,
  VolatilityResponse,
} from '../types';

type Toast = (message: string, tone?: 'success' | 'error') => void;

// The desk's chart palette (see Dashboard).
const AXIS_TICK = { fill: '#6a6a58', fontSize: 10, fontFamily: 'IBM Plex Mono' };
const GRID = '#2a2c22';
const AXIS_LINE = '#3d4032';
const DIM = '#9d9c88';
const AMBER = '#ffb000';
const TOOLTIP_STYLE = {
  background: '#1a1c16',
  border: '1px solid #3d4032',
  borderRadius: 0,
  fontSize: 12,
  fontFamily: 'IBM Plex Mono',
};

const pct = (value: number | null | undefined, digits = 1): string =>
  value === null || value === undefined || !Number.isFinite(value) ? '—' : `${value.toFixed(digits)}%`;

/**
 * Options analytics on model prices: the volatility premium, a strategy builder
 * and a chain. Nothing here is a live chain. Every contract is priced with
 * Black-Scholes at a VIX-anchored, skewed volatility, by the same function the
 * options stream trades and is valued with.
 */
export default function Options({ pushToast }: { pushToast: Toast }) {
  const [input, setInput] = useState('SPY');
  const [symbol, setSymbol] = useState('SPY');
  const volatility = usePolling<VolatilityResponse>(() => api.optionsVolatility(), 120_000);
  const presets = usePolling<{ presets: OptionPreset[] }>(() => api.optionsPresets(), null);

  function load(): void {
    const next = input.trim().toUpperCase();
    if (!/^[A-Z]{1,6}$/.test(next)) {
      pushToast('Options are modelled on US tickers only, such as SPY or AAPL.', 'error');
      return;
    }
    setSymbol(next);
  }

  return (
    <div className="page">
      <div className="page-head">
        <div>
          <h1>Options</h1>
          <p>
            The volatility premium behind the options stream, a strategy builder for the standard
            structures, and a chain for any US ticker. Everything is priced, valued and settled the
            same way the stream trades.
          </p>
        </div>
      </div>

      <Note tone="info" title="Model prices, not a live feed">
        No free, reliable options feed exists, so every contract is priced with Black-Scholes-Merton.
        SPY uses the live VIX scaled to at-the-money ({volatility.data ? `VIX ÷ ${volatility.data.vixToAtmRatio}` : 'VIX ÷ 1.15'})
        plus a put skew; single stocks use their own realised volatility with no premium added, as the
        research suggests. Bids and asks sit around that model mid.
      </Note>

      {volatility.data ? (
        <VolatilityPanel data={volatility.data} />
      ) : volatility.error ? (
        <Note tone="warn" title="Volatility data unavailable">
          {volatility.error}
        </Note>
      ) : (
        <Spinner label="Loading volatility" />
      )}

      <Panel title="Underlying">
        <form
          className="inline-form"
          onSubmit={(event) => {
            event.preventDefault();
            load();
          }}
        >
          <div className="field" style={{ maxWidth: 170 }}>
            <label htmlFor="opt-symbol">US ticker</label>
            <input
              id="opt-symbol"
              type="text"
              value={input}
              autoComplete="off"
              spellCheck={false}
              onChange={(event) => setInput(event.target.value)}
            />
            <span className="field-hint">SPY carries the index premium</span>
          </div>
          <button className="btn btn-primary" type="submit">
            Load
          </button>
        </form>
      </Panel>

      <StrategyBuilder symbol={symbol} presets={presets.data?.presets ?? []} pushToast={pushToast} />
      <ChainViewer symbol={symbol} />
      <EvidencePanel />
    </div>
  );
}

// --- volatility premium -----------------------------------------------------

function VolatilityPanel({ data }: { data: VolatilityResponse }) {
  const { market, premium } = data;
  const gap =
    premium.meanVix !== null && premium.meanRealised !== null ? premium.meanVix - premium.meanRealised : null;
  const vixFoot =
    market.vixSource === 'live'
      ? `live · ${formatRelative(market.vixAsOf)}`
      : market.vixSource === 'stored'
        ? `last close ${market.vixAsOf ?? ''}`
        : 'unavailable';

  return (
    <Panel title="Volatility premium" note="implied against what followed">
      <div className="grid grid-4">
        <Figure label="VIX" value={market.vix === null ? '—' : market.vix.toFixed(2)} foot={vixFoot} />
        <Figure
          label="SPY at-the-money implied"
          value={pct(data.atmVolPct)}
          foot={
            data.atmSource === 'vix'
              ? `VIX ÷ ${data.vixToAtmRatio}`
              : data.atmSource === 'realised'
                ? 'from realised: no VIX'
                : 'no data: 25% assumed'
          }
        />
        <Figure
          label="SPY realised 21d / 63d"
          value={`${pct(data.spy.realised21Pct)} / ${pct(data.spy.realised63Pct)}`}
          foot={`${data.spy.bars} daily bars stored`}
        />
        <Figure
          label="Premium, past year"
          value={gap === null ? '—' : `${gap >= 0 ? '+' : ''}${gap.toFixed(1)} pts`}
          tone={gap === null ? 'flat' : gap > 0 ? 'up' : 'down'}
          foot={
            gap === null
              ? 'needs stored SPY and VIX history'
              : `realised beat VIX on ${pct(premium.realisedAboveVixPct, 0)} of days`
          }
        />
      </div>

      {data.history.length > 20 ? (
        <div
          className="chart-frame-sm"
          style={{ marginTop: 14 }}
          role="img"
          aria-label={`VIX against SPY volatility realised over the following 21 trading days, for the past ${data.history.length} days. Mean VIX ${formatNumber(premium.meanVix, 1)}, mean realised ${formatNumber(premium.meanRealised, 1)}.`}
        >
          <ResponsiveContainer width="100%" height="100%">
            <LineChart data={data.history} margin={{ top: 6, right: 14, bottom: 4, left: 4 }}>
              <CartesianGrid stroke={GRID} strokeDasharray="2 4" />
              <XAxis dataKey="date" tick={AXIS_TICK} stroke={AXIS_LINE} minTickGap={48} />
              <YAxis tick={AXIS_TICK} stroke={AXIS_LINE} width={40} domain={[0, 'auto']} />
              <Tooltip
                contentStyle={TOOLTIP_STYLE}
                formatter={(value) => (typeof value === 'number' ? value.toFixed(2) : String(value))}
              />
              <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
              <Line type="monotone" dataKey="vix" name="VIX (implied, 30 days)" stroke={AMBER} strokeWidth={1.75} dot={false} />
              <Line
                type="monotone"
                dataKey="realisedNext21"
                name="SPY realised, next 21 days"
                stroke={DIM}
                strokeWidth={1.25}
                dot={false}
              />
            </LineChart>
          </ResponsiveContainer>
        </div>
      ) : (
        <Empty>
          The history chart needs stored SPY bars and the Cboe VIX series. Run `npm run backfill:deep
          -w server` to load them.
        </Empty>
      )}
      <p className="prose" style={{ marginTop: 10 }}>
        When the amber line sits above the grey one, options were priced for more movement than
        arrived, and sellers kept the difference. From 2016 to 2026 VIX averaged 18.7 against 15.0
        realised, but realised beat VIX on about one day in six, and those days clustered in
        sell-offs.
      </p>
    </Panel>
  );
}

// --- strategy builder ---------------------------------------------------------

interface BuilderInputs {
  preset: OptionPresetId;
  expiry: string;
  contracts: string;
  width: string;
}

function StrategyBuilder({
  symbol,
  presets,
  pushToast,
}: {
  symbol: string;
  presets: OptionPreset[];
  pushToast: Toast;
}) {
  const [inputs, setInputs] = useState<BuilderInputs>({
    preset: 'cash_secured_put',
    expiry: '',
    contracts: '1',
    width: '5',
  });
  const [result, setResult] = useState<AnalyseResponse | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);

  async function run(next: BuilderInputs): Promise<void> {
    const contracts = Number(next.contracts);
    const width = Number(next.width);
    if (!(contracts >= 0.01 && contracts <= 100)) {
      pushToast('Contracts must be between 0.01 and 100.', 'error');
      return;
    }
    if (!(width >= 0.5 && width <= 50)) {
      pushToast('Strike distance must be between 0.5% and 50%.', 'error');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      const response = await api.analyseStrategy({
        symbol,
        preset: next.preset,
        ...(next.expiry ? { expiry: next.expiry } : {}),
        contracts,
        widthPct: width / 100,
      });
      setResult(response);
    } catch (err) {
      setResult(null);
      setError(err instanceof Error ? err.message : 'Analysis failed');
    } finally {
      setBusy(false);
    }
  }

  // A new underlying has its own expiries, so start again from the default one.
  useEffect(() => {
    const next = { ...inputs, expiry: '' };
    setInputs(next);
    void run(next);
    // Only the underlying triggers an automatic run; the form has its own button.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [symbol]);

  const set = (patch: Partial<BuilderInputs>) => setInputs((current) => ({ ...current, ...patch }));
  const preset = presets.find((entry) => entry.id === inputs.preset);
  const analysis = result?.analysis;

  return (
    <Panel title="Strategy builder" note={`${symbol} · payoff, Greeks and risk at model prices`}>
      <form
        className="inline-form"
        onSubmit={(event) => {
          event.preventDefault();
          void run(inputs);
        }}
      >
        <div className="field" style={{ flex: '1 1 220px' }}>
          <label htmlFor="opt-preset">Structure</label>
          <select
            id="opt-preset"
            value={inputs.preset}
            onChange={(event) => set({ preset: event.target.value as OptionPresetId })}
          >
            {presets.length === 0 && <option value="cash_secured_put">Cash-secured put</option>}
            {presets.map((entry) => (
              <option key={entry.id} value={entry.id}>
                {entry.name}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ maxWidth: 230 }}>
          <label htmlFor="opt-expiry">Expiry</label>
          <select id="opt-expiry" value={inputs.expiry} onChange={(event) => set({ expiry: event.target.value })}>
            <option value="">nearest to 30 days</option>
            {(result?.expiries ?? []).map((entry) => (
              <option key={entry.expiry} value={entry.expiry}>
                {entry.expiry} · {Math.round(entry.days)}d{entry.monthly ? ' · monthly' : ''}
              </option>
            ))}
          </select>
        </div>
        <div className="field" style={{ maxWidth: 120 }}>
          <label htmlFor="opt-contracts">Contracts</label>
          <input
            id="opt-contracts"
            type="number"
            min="0.01"
            max="100"
            // "any": with min 0.01, a step of 1 would make whole numbers a step
            // mismatch, and the browser would silently refuse to submit.
            step="any"
            value={inputs.contracts}
            onChange={(event) => set({ contracts: event.target.value })}
          />
        </div>
        <div className="field" style={{ maxWidth: 170 }}>
          <label htmlFor="opt-width">Strike distance (%)</label>
          <input
            id="opt-width"
            type="number"
            min="0.5"
            max="50"
            step="0.5"
            value={inputs.width}
            aria-describedby="opt-width-help"
            onChange={(event) => set({ width: event.target.value })}
          />
        </div>
        <button className="btn btn-primary" type="submit" disabled={busy}>
          {busy ? <Spinner /> : 'Analyse'}
        </button>
      </form>

      <p id="opt-width-help" className="field-hint" style={{ marginTop: 6 }}>
        Strike distance is how far the out-of-the-money strikes sit from spot, as a percentage of the
        price.
      </p>
      {preset && (
        <p className="prose" style={{ marginTop: 10 }}>
          <strong style={{ color: 'var(--ink)' }}>{preset.view}.</strong> {preset.evidence}
        </p>
      )}
      {error && (
        <Note tone="warn" title="Could not analyse">
          {error}
        </Note>
      )}
      {analysis && <AnalysisView analysis={analysis} />}
    </Panel>
  );
}

function AnalysisView({ analysis }: { analysis: StrategyAnalysis }) {
  const premiumLabel =
    analysis.netPremiumKind === 'credit' ? 'Net credit' : analysis.netPremiumKind === 'debit' ? 'Net debit' : 'Net premium';
  const breakevens =
    analysis.breakevens.length === 0 ? '—' : analysis.breakevens.map((value) => formatUsd(value)).join(' / ');

  return (
    <>
      <div className="grid grid-4" style={{ marginTop: 14 }}>
        <Figure
          label={premiumLabel}
          value={formatUsd(Math.abs(analysis.netPremium))}
          foot={`at mid; the spread costs ${formatUsd(analysis.spreadCost)} more`}
        />
        <Figure
          label="Max profit"
          value={analysis.maxProfit === null ? 'unlimited' : formatUsd(analysis.maxProfit)}
          tone="up"
          foot={`by the ${analysis.evaluationExpiry} expiry`}
        />
        <Figure
          label="Max loss"
          value={analysis.maxLoss === null ? 'unlimited' : formatUsd(analysis.maxLoss)}
          tone="down"
          foot={analysis.risk === 'defined' ? 'defined risk' : 'undefined risk'}
        />
        <Figure label="Breakeven at expiry" value={breakevens} foot={`spot ${formatUsd(analysis.spot)}`} />
      </div>

      {analysis.warnings.map((warning) => (
        <div key={warning} style={{ marginTop: 10 }}>
          <Note tone="warn">{warning}</Note>
        </div>
      ))}

      <div
        className="chart-frame"
        style={{ marginTop: 14 }}
        role="img"
        aria-label={`Profit and loss of the position against the ${analysis.underlying} price, at the ${analysis.evaluationExpiry} expiry and today. Max profit ${analysis.maxProfit === null ? 'unlimited' : formatUsd(analysis.maxProfit)}, max loss ${analysis.maxLoss === null ? 'unlimited' : formatUsd(analysis.maxLoss)}, breakeven ${breakevens}.`}
      >
        <PayoffChart analysis={analysis} />
      </div>

      <div className="split-73" style={{ marginTop: 14 }}>
        <div className="grid-wrap">
          <table aria-label="Strategy legs">
            <thead>
              <tr>
                <th scope="col">Leg</th>
                <th scope="col" className="num">
                  Mid
                </th>
                <th scope="col" className="num">
                  Bid / ask
                </th>
                <th scope="col" className="num">
                  IV
                </th>
                <th scope="col" className="num">
                  Delta
                </th>
              </tr>
            </thead>
            <tbody>
              {analysis.legs.map((leg, index) => (
                <tr key={`${leg.description}-${index}`}>
                  <td>{leg.description}</td>
                  <td className="num">{formatUsd(leg.entry)}</td>
                  <td className="num">
                    {leg.bid !== undefined && leg.ask !== undefined ? `${leg.bid.toFixed(2)} / ${leg.ask.toFixed(2)}` : '—'}
                  </td>
                  <td className="num">{leg.iv === undefined ? '—' : pct(leg.iv * 100)}</td>
                  <td className="num">
                    {leg.delta !== undefined ? leg.delta.toFixed(2) : leg.kind === 'stock' ? '1.00' : '—'}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <dl className="kv">
          <dt>Delta</dt>
          <dd>{formatNumber(analysis.greeks.delta, 1)} shares</dd>
          <dt>Gamma</dt>
          <dd>{formatNumber(analysis.greeks.gamma, 2)} per $1</dd>
          <dt>Theta</dt>
          <dd>{formatUsd(analysis.greeks.theta)} a day</dd>
          <dt>Vega</dt>
          <dd>{formatUsd(analysis.greeks.vega)} per vol point</dd>
          <dt>Capital at risk</dt>
          <dd>{analysis.capitalAtRisk === null ? 'unlimited' : formatUsd(analysis.capitalAtRisk)}</dd>
          {analysis.stockCost !== 0 && (
            <>
              <dt>Stock cost</dt>
              <dd>{formatUsd(analysis.stockCost)}</dd>
            </>
          )}
        </dl>
      </div>
    </>
  );
}

function PayoffChart({ analysis }: { analysis: StrategyAnalysis }) {
  return (
    <ResponsiveContainer width="100%" height="100%">
      <LineChart data={analysis.curve} margin={{ top: 14, right: 14, bottom: 4, left: 4 }}>
        <CartesianGrid stroke={GRID} strokeDasharray="2 4" />
        <XAxis
          dataKey="price"
          type="number"
          domain={['dataMin', 'dataMax']}
          tick={AXIS_TICK}
          stroke={AXIS_LINE}
          tickFormatter={(value: number) => `$${Math.round(value)}`}
          minTickGap={36}
        />
        <YAxis
          tick={AXIS_TICK}
          stroke={AXIS_LINE}
          width={76}
          tickFormatter={(value: number) => formatUsd(value, 0)}
        />
        <Tooltip
          contentStyle={TOOLTIP_STYLE}
          labelFormatter={(label) =>
            typeof label === 'number' ? `${analysis.underlying} at ${formatUsd(label)}` : String(label)
          }
          formatter={(value) => (typeof value === 'number' ? formatUsd(value) : String(value))}
        />
        <Legend wrapperStyle={{ fontSize: 11, fontFamily: 'IBM Plex Mono' }} />
        <ReferenceLine y={0} stroke={DIM} />
        <ReferenceLine
          x={analysis.spot}
          stroke={DIM}
          strokeDasharray="3 3"
          label={{ value: 'spot', fill: DIM, fontSize: 10, position: 'top' }}
        />
        <Line
          type="linear"
          dataKey="expiryPnl"
          name={`At expiry (${analysis.evaluationExpiry})`}
          stroke={AMBER}
          strokeWidth={2}
          dot={false}
        />
        <Line
          type="monotone"
          dataKey="todayPnl"
          name="Today (model)"
          stroke={DIM}
          strokeWidth={1.25}
          strokeDasharray="4 3"
          dot={false}
        />
      </LineChart>
    </ResponsiveContainer>
  );
}

// --- chain --------------------------------------------------------------------

function ChainViewer({ symbol }: { symbol: string }) {
  const chain = usePolling<OptionChainResponse>(() => api.optionsChain(symbol), 120_000, [symbol]);
  const [selected, setSelected] = useState<string | null>(null);

  useEffect(() => setSelected(null), [symbol]);

  const data = chain.data && chain.data.chain.underlying === symbol ? chain.data : null;
  const expiries = data?.chain.expiries ?? [];
  // Default to the standard monthly nearest a month out, the stream's own choice.
  const active =
    expiries.find((entry) => entry.expiry === selected) ??
    expiries.find((entry) => entry.monthly && entry.days >= 20) ??
    expiries[0];

  return (
    <Panel
      title="Chain"
      note={
        data
          ? `${data.chain.underlying} ${formatUsd(data.chain.spot)}${data.quote.stale ? ' (stale)' : ''} · ` +
            `ATM IV ${pct(data.chain.vol.atmVol * 100)} · ${data.chain.vol.note}`
          : symbol
      }
    >
      {chain.error && !data && (
        <Note tone="warn" title={`No chain for ${symbol}`}>
          {chain.error}
        </Note>
      )}
      {!data && !chain.error && <Spinner label={`Pricing ${symbol}`} />}
      {data && active && (
        <>
          <div className="tabs" role="tablist" aria-label="Expiries">
            {expiries.map((entry) => (
              <button
                key={entry.expiry}
                type="button"
                role="tab"
                aria-selected={entry.expiry === active.expiry}
                className={`tab${entry.expiry === active.expiry ? ' active' : ''}`}
                onClick={() => setSelected(entry.expiry)}
              >
                {entry.expiry.slice(5)} · {Math.round(entry.days)}d{entry.monthly ? ' · M' : ''}
              </button>
            ))}
          </div>
          <ChainTable expiry={active} spot={data.chain.spot} />
          <p className="prose" style={{ marginTop: 10 }}>
            Calls on the left, puts on the right, per share; in-the-money cells are shaded and the
            row nearest the money is outlined. M marks the standard monthly (third-Friday) expiry the
            options stream sells. Rate {pct(data.chain.rate * 100, 2)} continuous.
          </p>
        </>
      )}
    </Panel>
  );
}

function ChainTable({ expiry, spot }: { expiry: ChainExpiry; spot: number }) {
  const atm = expiry.rows.reduce(
    (best, row) => (Math.abs(row.strike - spot) < Math.abs(best - spot) ? row.strike : best),
    expiry.rows[0]?.strike ?? spot,
  );
  const shade = { background: 'var(--amber-wash)' };
  const columns = ['Bid', 'Ask', 'IV', 'Delta'];

  return (
    <div className="grid-wrap" style={{ maxHeight: 520, overflowY: 'auto', marginTop: 10 }}>
      <table aria-label={`Model option chain, ${expiry.expiry} expiry`}>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={`call-${column}`} scope="col" className="num">
                Call {column.toLowerCase()}
              </th>
            ))}
            <th scope="col" className="num" style={{ textAlign: 'center' }}>
              Strike
            </th>
            {columns.map((column) => (
              <th key={`put-${column}`} scope="col" className="num">
                Put {column.toLowerCase()}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {expiry.rows.map((row) => {
            const callShade = row.strike < spot ? shade : undefined;
            const putShade = row.strike > spot ? shade : undefined;
            return (
              <tr
                key={row.strike}
                style={row.strike === atm ? { outline: '1px solid var(--amber-deep)', outlineOffset: -1 } : undefined}
              >
                <td className="num" style={callShade}>{row.call.bid.toFixed(2)}</td>
                <td className="num" style={callShade}>{row.call.ask.toFixed(2)}</td>
                <td className="num" style={callShade}>{pct(row.call.iv * 100)}</td>
                <td className="num" style={callShade}>{row.call.delta.toFixed(2)}</td>
                <th scope="row" className="num sym" style={{ textAlign: 'center' }}>
                  {row.strike}
                </th>
                <td className="num" style={putShade}>{row.put.bid.toFixed(2)}</td>
                <td className="num" style={putShade}>{row.put.ask.toFixed(2)}</td>
                <td className="num" style={putShade}>{pct(row.put.iv * 100)}</td>
                <td className="num" style={putShade}>{row.put.delta.toFixed(2)}</td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

// --- evidence -------------------------------------------------------------------

const EVIDENCE: Array<{ series: string; cagr: string; vol: string; sharpe: string; drawdown: string }> = [
  { series: 'SPY buy and hold (price only)', cagr: '13.3%', vol: '18.2%', sharpe: '0.78', drawdown: '-34.1%' },
  { series: 'Cboe PUT index (real, earns T-bill interest)', cagr: '8.4%', vol: '12.9%', sharpe: '0.69', drawdown: '-28.9%' },
  { series: 'Options stream rule (simulated, no interest)', cagr: '5.8%', vol: '11.8%', sharpe: '0.54', drawdown: '-26.8%' },
  { series: 'Long 30-delta calls (control)', cagr: '3.6%', vol: '32.5%', sharpe: '0.27', drawdown: '-46.8%' },
];

function EvidencePanel() {
  return (
    <Panel title="What the evidence says" note="evidence check · SPY Jan 2017 to Oct 2026 · £100k">
      <div className="grid-wrap">
        <table aria-label="Put-write evidence, SPY 2017 to 2026">
          <thead>
            <tr>
              <th scope="col">Series</th>
              <th scope="col" className="num">
                CAGR
              </th>
              <th scope="col" className="num">
                Volatility
              </th>
              <th scope="col" className="num">
                Sharpe
              </th>
              <th scope="col" className="num">
                Worst drawdown
              </th>
            </tr>
          </thead>
          <tbody>
            {EVIDENCE.map((row) => (
              <tr key={row.series}>
                <th scope="row" style={{ textAlign: 'left', fontWeight: 400 }}>
                  {row.series}
                </th>
                <td className="num">{row.cagr}</td>
                <td className="num">{row.vol}</td>
                <td className="num">{row.sharpe}</td>
                <td className="num down">{row.drawdown}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <ul className="prose" style={{ marginTop: 12, paddingLeft: 18 }}>
        <li>
          The simulator tracks the real PUT index (daily-return correlation 0.97). Its 2.6-point annual
          shortfall matches the T-bill interest the index earns on its collateral, which this app does
          not pay on cash.
        </li>
        <li>
          Over this bull-market decade, put-writing trailed simply holding SPY, at about two-thirds of
          the volatility. Walk-forward alpha against SPY was negative in every fold: it is a
          lower-volatility way to hold equity risk, not an edge over the market.
        </li>
        <li>
          None of the pre-registered variants (30- or 15-delta, weekly, take profit at 50%, a 200-day
          filter) beat the at-the-money monthly rule on risk-adjusted return, so the stream keeps it.
        </li>
        <li>Buying options is the losing side on average: the long-call control returned less, with far deeper drawdowns.</li>
        <li>
          Simplifications: European pricing, cash settlement at intrinsic value, no early assignment,
          fractional contracts, and no volatility term structure (weeklies are priced off the 30-day
          VIX, which understates their swings).
        </li>
      </ul>
    </Panel>
  );
}
